import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import Header from "../components/Header";
import ResultsDashboard from "../components/ResultsDashboard";
import { ApiError, HTTP_STATUS_NOT_FOUND, fetchRun } from "../lib/api";
import { categoriesStateFromRun, type RunDetail, type RunStatus } from "../types";

const GENERIC_LOAD_ERROR = "Falha ao carregar a pesquisa.";
const NOT_FOUND_ERROR = "Pesquisa não encontrada.";

const STATUS_LABELS: Record<RunStatus, string> = {
  processing: "Em processamento",
  completed: "Concluída",
  partial: "Parcial",
  failed: "Falha",
};

const dateTimeFormatter = new Intl.DateTimeFormat("pt-BR", {
  dateStyle: "short",
  timeStyle: "short",
});

function BackLink() {
  return (
    <Link
      to="/history"
      className="text-sm font-medium text-ink-400 hover:text-ink-100 font-body transition-colors"
    >
      Voltar ao histórico
    </Link>
  );
}

export default function RunDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [detail, setDetail] = useState<RunDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!id) return;
    let active = true;

    async function load() {
      setIsLoading(true);
      setError(null);
      setNotFound(false);
      try {
        const run = await fetchRun(id as string);
        if (active) setDetail(run);
      } catch (err) {
        console.error("Falha ao carregar a pesquisa:", err);
        if (!active) return;
        if (err instanceof ApiError && err.status === HTTP_STATUS_NOT_FOUND) {
          setNotFound(true);
        } else {
          setError(GENERIC_LOAD_ERROR);
        }
      } finally {
        if (active) setIsLoading(false);
      }
    }

    load();
    return () => {
      active = false;
    };
  }, [id]);

  return (
    <div className="min-h-screen relative" style={{ backgroundColor: "var(--color-background)" }}>
      <div
        className="fixed inset-0 z-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: "url(/tavily_landscapes_edited_11.webp)",
          opacity: 0.7,
          willChange: "transform",
          transform: "translateZ(0)",
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-8">
        <Header />

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mt-10 space-y-4"
        >
          <BackLink />

          {isLoading && (
            <div className="glass rounded-2xl p-10 flex items-center justify-center">
              <Loader2 className="w-5 h-5 text-ink-400 animate-spin" />
            </div>
          )}

          {!isLoading && notFound && (
            <div className="glass rounded-2xl p-10 text-center space-y-3">
              <p className="text-ink-100 font-body font-medium">{NOT_FOUND_ERROR}</p>
              <BackLink />
            </div>
          )}

          {!isLoading && !notFound && error && (
            <div
              role="alert"
              className="rounded-xl border border-red-300/60 bg-red-50/80 px-4 py-3 font-body"
            >
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {!isLoading && !notFound && !error && detail && (
            <>
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="font-display text-xl text-ink-100">{detail.company_name}</h1>
                <span className="text-xs font-body text-ink-500">
                  {STATUS_LABELS[detail.status]}
                </span>
                <span className="text-xs font-body text-ink-500">
                  {dateTimeFormatter.format(new Date(detail.created_at))}
                </span>
              </div>

              <ResultsDashboard
                categories={categoriesStateFromRun(detail)}
                companyName={detail.company_name}
                autoAdvance={false}
              />
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
